import { useState, useEffect, useCallback } from 'react';
import { sampleDataManager, SampleDataPreferences, SampleDataContext } from '../lib/sampleDataManager';

export interface UseSampleDataReturn {
  preferences: SampleDataPreferences | null;
  isLoading: boolean;
  shouldShowPrompt: (feature: string) => boolean;
  shouldShowSampleData: (feature: string) => boolean;
  showSampleDataPrompt: (feature: string) => Promise<boolean>;
  dismissSampleDataPrompt: (feature: string) => Promise<void>;
  setSampleDataEnabled: (enabled: boolean) => Promise<void>;
  setSampleDataPromptsEnabled: (enabled: boolean) => Promise<void>;
  getSampleDataContext: (feature: string) => SampleDataContext | null;
  resetPreferences: () => Promise<void>;
  refreshPreferences: () => Promise<void>;
}

export const useSampleData = (): UseSampleDataReturn => {
  const [preferences, setPreferences] = useState<SampleDataPreferences | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshPreferences = useCallback(async () => {
    try {
      setIsLoading(true);
      const prefs = await sampleDataManager.getUserPreferences();
      setPreferences(prefs);
    } catch (error) {
      console.error('Error loading sample data preferences:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshPreferences();
  }, [refreshPreferences]);

  const shouldShowPrompt = useCallback((feature: string): boolean => {
    if (!preferences) return false;
    
    // If user has disabled prompts, don't show
    if (!preferences.showSampleDataPrompts) {
      return false;
    }

    // If user has dismissed this feature, don't show
    if (preferences.dismissedFeatures.includes(feature)) {
      return false;
    }

    // Check cooldown period
    if (preferences.lastPromptDate) {
      const lastPrompt = new Date(preferences.lastPromptDate);
      const daysSinceLastPrompt = (Date.now() - lastPrompt.getTime()) / (1000 * 60 * 60 * 24);
      if (daysSinceLastPrompt < 7) { // 7 day cooldown
        return false;
      }
    }

    return true;
  }, [preferences]);

  const shouldShowSampleData = useCallback((feature: string): boolean => {
    if (!preferences) return false;
    return preferences.showSampleData;
  }, [preferences]);

  const showSampleDataPrompt = useCallback(async (feature: string): Promise<boolean> => {
    return await sampleDataManager.showSampleDataPrompt(feature);
  }, []);

  const dismissSampleDataPrompt = useCallback(async (feature: string): Promise<void> => {
    await sampleDataManager.dismissSampleDataPrompt(feature);
    await refreshPreferences();
  }, [refreshPreferences]);

  const setSampleDataEnabled = useCallback(async (enabled: boolean): Promise<void> => {
    await sampleDataManager.setSampleDataEnabled(enabled);
    await refreshPreferences();
  }, [refreshPreferences]);

  const setSampleDataPromptsEnabled = useCallback(async (enabled: boolean): Promise<void> => {
    await sampleDataManager.setSampleDataPromptsEnabled(enabled);
    await refreshPreferences();
  }, [refreshPreferences]);

  const getSampleDataContext = useCallback((feature: string): SampleDataContext | null => {
    return sampleDataManager.getSampleDataContext(feature);
  }, []);

  const resetPreferences = useCallback(async (): Promise<void> => {
    await sampleDataManager.resetPreferences();
    await refreshPreferences();
  }, [refreshPreferences]);

  return {
    preferences,
    isLoading,
    shouldShowPrompt,
    shouldShowSampleData,
    showSampleDataPrompt,
    dismissSampleDataPrompt,
    setSampleDataEnabled,
    setSampleDataPromptsEnabled,
    getSampleDataContext,
    resetPreferences,
    refreshPreferences
  };
}; 